import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { CSSProperties, ForwardedRef, forwardRef, MouseEvent, ReactNode, TouchEvent } from 'react'
import {
  getGapStyle,
  getMarginStyle,
  getPaddingStyle,
  getPinStyle,
  Margin,
  Padding,
  Position,
  styles,
} from 'util/style'
import css from './Space.module.scss'

export type SpaceProps = {
  center?: boolean
  children?: ReactNode
  className?: string
  columns?: boolean
  fit?: boolean
  flex?: string
  gap?: number | [number, number]
  height?: number | string
  margin?: Margin
  maxHeight?: number | string
  maxWidth?: number | string
  minHeight?: number | string
  minWidth?: number | string
  oh?: boolean
  onClick?(event?: MouseEvent): unknown
  opposite?: boolean
  pad?: Padding
  pic?: boolean
  pin?: Position
  right?: boolean
  panel?: boolean
  parallel?: boolean
  round?: number | boolean
  shrink?: boolean
  spread?: boolean
  spring?: boolean
  stable?: boolean
  style?: CSSProperties
  tall?: boolean
  title?: string
  top?: boolean
  vertical?: boolean
  wide?: boolean
  width?: number | string
  wrap?: boolean
  z?: number
  bottom?: boolean
  size?: number | string
}

function buildStyle(props: SpaceProps) {
  const {
    flex,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    pad,
    round,
    style,
    z,
  } = props
  const width = props.width ?? props.size
  const height = props.height ?? props.size
  const gap = getGapStyle(props.gap)
  const padding = getPaddingStyle(pad)
  const margin = getMarginStyle(props.margin)
  const pin = getPinStyle(props.pin)
  const borderRadius = typeof round === 'number' ? round : undefined
  const border = { borderRadius }
  return styles({
    flex,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    zIndex: z,
    ...gap,
    ...padding,
    ...margin,
    ...pin,
    ...border,
    ...style,
  })
}

export const Space = forwardRef(
  (props: SpaceProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      center,
      children,
      columns,
      fit,
      oh,
      onClick,
      opposite,
      right,
      top,
      panel,
      parallel,
      pic,
      pin,
      round,
      shrink,
      spread,
      spring,
      stable,
      tall,
      title,
      vertical,
      wide,
      wrap,
      bottom,
    } = props

    const style = buildStyle(props)
    const className = clsx(
      props.className,
      vertical ? css.column : css.row,
      !!onClick && css.action,
      top && css.top,
      bottom && css.bottom,
      center && css.center,
      right && css.right,
      oh && css.oh,
      opposite && css.opposite,
      panel && css.panel,
      round === true && css.round,
      shrink && css.shrink,
      spread && css.spread,
      stable && css.stable,
      columns && css.columns,
      fit && css.fit,
      wide && css.wide,
      spring && css.spring,
      tall && css.tall,
      wrap && css.wrap,
      parallel && css.parallel,
      pic && css.pic,
    )

    const onMouseClick = useLatestCallback((event: MouseEvent) => {
      if (pin) event.stopPropagation()
      if (onClick) onClick(event)
    })

    const onMouseDown = useLatestCallback((event: MouseEvent) => {
      if (pin) event.stopPropagation()
    })

    const onTouchStart = useLatestCallback((event: TouchEvent) => {
      if (pin) event.stopPropagation()
    })

    return (
      <div
        ref={ref}
        className={className}
        style={style}
        title={title}
        onClick={onMouseClick}
        onMouseDownCapture={onMouseDown}
        onTouchStartCapture={onTouchStart}
      >
        {children}
      </div>
    )
  },
)
